import { useState, createContext, useContext, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import API_BASE from '../config';
import {
  Zap, LayoutDashboard, Megaphone, Users, Contact, BarChart3,
  LogOut, Activity, ClipboardList, Settings, Link as LinkIcon,
  TrendingUp, FileText, GitBranch, Receipt, ShieldCheck, UsersRound, Menu, X, Radio,
} from 'lucide-react';

export const ThemeContext = createContext({ theme: 'light', setTheme: () => {} });
export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(
    () => localStorage.getItem('martech_theme') || 'light'
  );
  const setTheme = useCallback((t) => {
    localStorage.setItem('martech_theme', t);
    setThemeState(t);
  }, []);
  useEffect(() => {
    document.body.style.margin = '0';
    document.body.style.fontFamily = "'Inter', system-ui, sans-serif";
    document.body.style.background = theme === 'dark' ? '#0D0D14' : '#F5F6FA';
  }, [theme]);
  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const SidebarContext = createContext({ expanded: true });
export const useSidebar = () => useContext(SidebarContext);
export const SidebarProvider = ({ children }) => (
  <SidebarContext.Provider value={{ expanded: true }}>{children}</SidebarContext.Provider>
);

const NAV = [
  { section: 'Overview', items: [
    { icon: LayoutDashboard, label: 'Dashboard',       to: '/dashboard'   },
  ]},
  { section: 'Engage', items: [
    { icon: Megaphone,       label: 'Campaigns',       to: '/campaigns'   },
    { icon: Zap,             label: 'Automation',      to: '/automation', flag: 'automation' },
    { icon: LinkIcon,        label: 'Short Links',     to: '/short-links', flag: 'short_links' },
    { icon: Radio,           label: 'Channel Sync',    to: '/channel-sync', flag: 'channel_sync' },
  ]},
  { section: 'Audience', items: [
    { icon: Users,           label: 'Segments',        to: '/segments'    },
    { icon: Contact,         label: 'Profiles',        to: '/profiles'    },
  ]},
  { section: 'Intelligence', items: [
    { icon: BarChart3,       label: 'Analytics',       to: '/analytics'   },
    { icon: Activity,        label: 'Heatmap',         to: '/heatmap'     },
    { icon: TrendingUp,      label: 'Attribution',     to: '/attribution', flag: 'attribution' },
  ]},
  { section: 'Content', items: [
    { icon: FileText,        label: 'Content Manager', to: '/content', flag: 'content_manager' },
    { icon: GitBranch,       label: 'Journeys',        to: '/journeys', flag: 'journeys' },
  ]},
  { section: 'Compliance', items: [
    { icon: ShieldCheck,     label: 'Data Rights',     to: '/data-rights' },
    { icon: UsersRound,      label: 'Team',            to: '/team'        },
    { icon: ClipboardList,   label: 'Audit Log',       to: '/audit-log'   },
  ]},
  { section: 'System', items: [
    { icon: Settings,        label: 'Settings',        to: '/settings'    },
    { icon: Receipt,         label: 'Invoice',         to: '/invoice'     },
  ]},
];

const STATIC_CSS = `
  .mts-nav-scroll { scrollbar-width: none; overflow-y: auto; overflow-x: hidden; flex: 1; }
  .mts-nav-scroll::-webkit-scrollbar { display: none; }
  .mts-sidebar-desk { position: fixed; top: 0; left: 0; bottom: 0; width: 220px; z-index: 50; display: flex; flex-direction: column; }
  .mts-mobile-bar { display: none; }
  .mts-mobile-drawer { position: fixed; top: 0; left: 0; bottom: 0; width: 260px; z-index: 70; transform: translateX(-100%); transition: transform 0.25s ease; display: flex; flex-direction: column; }
  .mts-mobile-backdrop { position: fixed; inset: 0; z-index: 60; background: rgba(0,0,0,0.45); }
  @media (max-width: 768px) {
    .mts-sidebar-desk { display: none !important; }
    .mts-mobile-bar { display: flex !important; position: fixed; top: 0; left: 0; right: 0; height: 56px; z-index: 50; align-items: center; justify-content: space-between; padding: 0 16px; box-shadow: 0 1px 8px rgba(0,0,0,0.07); }
    .mts-page-offset { margin-left: 0 !important; padding-top: 56px !important; }
  }
`;

let cssInjected = false;
function injectStaticCSS() {
  if (cssInjected) return;
  const el = document.createElement('style');
  el.textContent = STATIC_CSS;
  document.head.appendChild(el);
  cssInjected = true;
}

const TK = {
  light: {
    bg: '#ffffff', border: '#ECEEF2',
    text: '#0F1623', sub: '#6B7280', muted: '#9CA3AF',
    accent: '#5B4EE8', accentBg: '#EEF0FD', accentTxt: '#4338CA',
    hover: '#F7F8FA', userBg: '#F5F6FA',
    logo: 'linear-gradient(135deg,#6C63FF,#5B4EE8)',
  },
  dark: {
    bg: '#131320', border: '#1E1E2E',
    text: '#F1F2F6', sub: '#8B8FA8', muted: '#4B5263',
    accent: '#7C72FF', accentBg: 'rgba(124,114,255,0.12)', accentTxt: '#A5A0FF',
    hover: '#1A1A2A', userBg: '#1A1A2A',
    logo: 'linear-gradient(135deg,#7C72FF,#5B4EE8)',
  },
};

function NavItem({ icon: Icon, label, to, active, onNav, onClose, tk }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      type="button"
      onClick={() => { onNav(to); if (onClose) onClose(); }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 9,
        width: '100%', padding: '7px 10px', marginBottom: 1,
        background: active ? tk.accentBg : hov ? tk.hover : 'transparent',
        border: 'none', borderRadius: 7, cursor: 'pointer',
        color: active ? tk.accentTxt : hov ? tk.text : tk.sub,
        fontSize: 13, fontWeight: active ? 600 : 400,
        transition: 'background 0.1s, color 0.1s',
        textAlign: 'left', position: 'relative',
      }}
    >
      {active && (
        <span style={{
          position: 'absolute', left: -8, top: '50%', transform: 'translateY(-50%)',
          width: 3, height: 16, background: tk.accent, borderRadius: '0 3px 3px 0',
        }} />
      )}
      <Icon size={15} strokeWidth={active ? 2.2 : 1.8}
        color={active ? tk.accent : hov ? tk.text : tk.sub} />
      {label}
    </button>
  );
}

function SidebarBody({ tk, pathname, onNav, onClose }) {
  const { flags } = useFeatureFlags();
  const user = (() => {
    try { return JSON.parse(localStorage.getItem('martech_user') || '{}'); }
    catch { return {}; }
  })();

  const handleLogout = () => {
    ['martech_token','martech_user','martech_totp_verified','token','role']
      .forEach(k => localStorage.removeItem(k));
    onNav('/login');
  };

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '100%',
      background: tk.bg, borderRight: `1px solid ${tk.border}`,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '16px 16px 12px', borderBottom: `1px solid ${tk.border}`,
        flexShrink: 0,
      }}>
        <div style={{
          width: 30, height: 30, borderRadius: 8, background: tk.logo, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 2px 8px rgba(91,78,232,0.3)',
        }}>
          <Zap size={15} color="#fff" strokeWidth={2.5} />
        </div>
        <span style={{ fontWeight: 700, fontSize: 15, color: tk.text, letterSpacing: '-0.3px' }}>
          MarTech
        </span>
        <span style={{
          fontSize: 9, fontWeight: 700, color: '#fff', background: tk.logo,
          padding: '2px 6px', borderRadius: 4, letterSpacing: '0.5px',
        }}>CDP</span>
      </div>

      <div className="mts-nav-scroll" style={{ padding: '8px 8px' }}>
        {NAV.map(({ section, items }) => {
          const visibleItems = items.filter((it) => !it.flag || flags[it.flag] !== false);
          if (visibleItems.length === 0) return null;
          return (
            <div key={section} style={{ marginBottom: 4 }}>
              <p style={{
                fontSize: 9, fontWeight: 700, color: tk.muted,
                letterSpacing: '0.09em', textTransform: 'uppercase',
                margin: 0, padding: '8px 10px 4px',
              }}>{section}</p>
              {visibleItems.map(({ icon, label, to }) => (
                <NavItem key={to} icon={icon} label={label} to={to}
                  active={pathname === to} onNav={onNav} onClose={onClose} tk={tk} />
              ))}
            </div>
          );
        })}
      </div>

      <div style={{ flexShrink: 0, padding: '8px 8px 10px', borderTop: `1px solid ${tk.border}` }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 9,
          padding: '9px 10px', borderRadius: 9, background: tk.userBg,
        }}>
          <div style={{
            width: 30, height: 30, borderRadius: 8, background: tk.logo, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontWeight: 700, fontSize: 13,
          }}>
            {(user.name || 'A')[0].toUpperCase()}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: 12, fontWeight: 600, color: tk.text,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{user.name || 'Admin User'}</div>
            <div style={{ fontSize: 11, color: tk.accent, fontWeight: 500, textTransform: 'capitalize' }}>
              {user.role || 'Admin'}
            </div>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            onMouseEnter={e => e.currentTarget.style.color = '#EF4444'}
            onMouseLeave={e => e.currentTarget.style.color = tk.sub}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: tk.sub, padding: 5, borderRadius: 6,
              display: 'flex', alignItems: 'center', transition: 'color 0.15s',
            }}
          >
            <LogOut size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}


/* -- Feature Flags Context ------------------------------------------ */
export const FeatureFlagsContext = createContext({ flags: {}, loading: true });
export const useFeatureFlags = () => useContext(FeatureFlagsContext);

export const FeatureFlagsProvider = ({ children }) => {
  const [flags, setFlags] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('martech_token') || localStorage.getItem('token');
    if (!token) { setLoading(false); return; }
    fetch(`${API_BASE}/api/v1/flags/mine`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { if (d.success) setFlags(d.flags || {}); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <FeatureFlagsContext.Provider value={{ flags, loading }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

/* Wrap a route's page content — shows a "not in your plan" message
   instead of the page when the given flag is explicitly false.
   Flags absent from the object (fail-open) still render normally. */
export function FeatureGate({ flagName, children }) {
  const { flags, loading } = useFeatureFlags();
  if (loading) return null;
  if (flags[flagName] === false) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

export default function Navbar() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const tk = TK[theme];
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => { injectStaticCSS(); }, []);

  const handleNav = (to) => { navigate(to); setMobileOpen(false); };

  return (
    <>
      <aside className="mts-sidebar-desk">
        <SidebarBody tk={tk} pathname={pathname} onNav={handleNav} onClose={null} />
      </aside>

      <div
        className="mts-mobile-bar"
        style={{ background: tk.bg, borderBottom: `1px solid ${tk.border}` }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <div style={{
            width: 28, height: 28, borderRadius: 7, background: tk.logo,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Zap size={13} color="#fff" strokeWidth={2.5} />
          </div>
          <span style={{ fontWeight: 700, fontSize: 15, color: tk.text }}>MarTech</span>
          <span style={{
            fontSize: 8, fontWeight: 700, color: '#fff', background: tk.logo,
            padding: '2px 5px', borderRadius: 4,
          }}>CDP</span>
        </div>
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          style={{
            background: 'none', border: `1px solid ${tk.border}`,
            borderRadius: 8, padding: 7, cursor: 'pointer',
            color: tk.sub, display: 'flex', alignItems: 'center',
          }}
        >
          <Menu size={18} />
        </button>
      </div>

      {mobileOpen && (
        <div
          className="mts-mobile-backdrop"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <div
        className="mts-mobile-drawer"
        style={{ transform: mobileOpen ? 'translateX(0)' : 'translateX(-100%)' }}
      >
        <button
          type="button"
          onClick={() => setMobileOpen(false)}
          style={{
            position: 'absolute', top: 14, right: 14, zIndex: 1,
            background: 'none', border: 'none', cursor: 'pointer',
            color: tk.sub, padding: 4,
          }}
        >
          <X size={18} />
        </button>
        <SidebarBody tk={tk} pathname={pathname} onNav={handleNav} onClose={() => setMobileOpen(false)} />
      </div>
    </>
  );
}
